// Engine/factory key the backend routes on.
export enum AiProvider {
  CatchDefault = '',
  OpenAi = 'openai',
  Gemini = 'gemini',
  Anthropic = 'claude',
  M365Copilot = 'm365copilot',
}

// Providers configured with an API key and an optional model override.
export const API_KEY_AI_PROVIDERS: ReadonlyArray<AiProvider> = [
  AiProvider.OpenAi,
  AiProvider.Gemini,
  AiProvider.Anthropic,
];

// Providers withheld from the picker for this release. The engine still routes them and stored
// configuration still works, so this hides the choice rather than removing the capability - an
// organisation already set to one keeps it, and sees it (see visibleAiProviderOptions).
export const RELEASE_HIDDEN_AI_PROVIDERS: ReadonlyArray<AiProvider> = [
  AiProvider.OpenAi,
  AiProvider.Gemini,
  AiProvider.Anthropic,
];

// Every provider the picker knows about, in display order. Filter with RELEASE_HIDDEN_AI_PROVIDERS
// before binding it to a dropdown.
export const AI_PROVIDER_OPTIONS: ReadonlyArray<{ label: string; value: AiProvider }> = [
  { label: 'Catch default', value: AiProvider.CatchDefault },
  { label: 'OpenAI', value: AiProvider.OpenAi },
  { label: 'Gemini', value: AiProvider.Gemini },
  { label: 'Anthropic', value: AiProvider.Anthropic },
  { label: 'M365 Copilot', value: AiProvider.M365Copilot },
];

// Keys the API stores the AI provider settings under, inside the IntelliCatch external link's
// attribute dictionary. They arrive with the link itself, so a component that already has the link
// can read the organisation's provider without a second call and without needing the admin-only
// ai-config permission. Every value in that dictionary is a string, including the booleans.
// These must stay in step with ExternalLinkConstants on the API side.
export const AI_LINK_ATTRIBUTE_KEYS = {
  providerType: 'aiProviderType',
  copilotConsentGranted: 'aiCopilotConsentGranted',
} as const;

export const AI_CONSENT_GRANTED_VALUE = 'true';

/**
 * True when the organisation's AI provider can actually answer.
 *
 * Only Copilot can be configured yet unusable: it runs through Catch's Entra application, and every
 * request fails at the token exchange until a Microsoft administrator approves that application for
 * the organisation's tenant. The API refuses those requests rather than quietly answering from a
 * Catch default, so anything offering the chat UI has to check this first or it will open a window
 * that fails on its first message.
 */
export function isAiProviderUsable(attributes: Record<string, unknown> | null | undefined): boolean {
  const provider = String(attributes?.[AI_LINK_ATTRIBUTE_KEYS.providerType] ?? '').toLowerCase();
  if (provider !== AiProvider.M365Copilot) {
    return true;
  }
  return String(attributes?.[AI_LINK_ATTRIBUTE_KEYS.copilotConsentGranted] ?? '').toLowerCase() === AI_CONSENT_GRANTED_VALUE;
}
